import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertCircle, Search, MapPin, X } from 'lucide-react';
import { locationDropdownService, CitySearchResult } from '../../../services/locationDropdownService';
import useDebounce from '../../../hooks/useDebounce';

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  selectedState?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  className?: string;
  error?: string;
  disabled?: boolean;
}

export const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  value,
  onChange,
  selectedState,
  placeholder = "Search cities...",
  label = "City",
  required = false,
  className = "",
  error,
  disabled = false
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<CitySearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Debounce search query
  const debouncedQuery = useDebounce(query, 300);

  // Search cities when query changes
  const searchCities = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await locationDropdownService.searchCities(searchQuery, selectedState);
      setSuggestions(results);
    } catch (err) {
      console.error('Error searching cities:', err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedState]);

  // Effect to search when debounced query changes
  useEffect(() => {
    if (debouncedQuery) {
      searchCities(debouncedQuery);
    } else {
      setSuggestions([]);
    }
  }, [debouncedQuery, searchCities]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (city: CitySearchResult) => {
    setQuery(city.name);
    onChange(city.name);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Handle clear
  const handleClear = () => {
    setQuery('');
    onChange('');
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync query with value prop
  useEffect(() => {
    if (value !== query) {
      setQuery(value);
    }
  }, [value]);

  return (
    <div className={`space-y-2 relative ${className}`}>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        {!required && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
      </label>
      
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            className={`h-12 w-full rounded-xl border bg-white px-4 py-3 pl-10 pr-10 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
              error
                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                : value
                ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                : disabled
                ? "border-gray-200 bg-gray-50 text-gray-400"
                : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            placeholder={placeholder}
          />
          
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {isLoading && (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-brand-500 rounded-full animate-spin"></div>
            )}
            {query && !isLoading && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
        
        {/* Dropdown */}
        {isOpen && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.name}-${index}`}
                type="button"
                onClick={() => handleSuggestionSelect(suggestion)}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                  index === highlightedIndex ? 'bg-gray-50' : ''
                } ${index === 0 ? 'rounded-t-xl' : ''} ${index === suggestions.length - 1 ? 'rounded-b-xl' : ''}`}
              >
                <MapPin className="w-4 h-4 text-brand-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {suggestion.name}
                  </div>
                  {suggestion.state && (
                    <div className="text-xs text-gray-500 truncate">
                      {suggestion.state}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
        
        {/* No results */}
        {isOpen && debouncedQuery.length >= 2 && !isLoading && suggestions.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4">
            <div className="flex items-center gap-3 text-gray-500">
              <Search className="w-5 h-5" />
              <span className="text-sm">No cities found for "{debouncedQuery}"</span>
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="flex items-center gap-1 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </p>
      )}
      
      {selectedState && !error && (
        <p className="text-sm text-gray-500">
          Searching in: {selectedState}
        </p>
      )}
    </div>
  );
};
